import * as Service from 'types/service';
import { BookingDragItem } from 'components/calendar/calendar-body/DraggableNormalCalendarSquare';
import { DRAG_ASSIGNMENT_TIMING } from 'utils/analytics/timings';
import { DndItemTypes } from 'constants/dndItemTypes';
import { Instant } from 'js-joda';
import { MatchParams } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { PartyDragData, PartyDragSource, trackDragStart } from '../Party';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { TableDropResult } from 'components/calendar/calendar-body/FlexBasedCalendarBody/FlexTableDropTarget/FlexTableDropTarget';
import { calendarStateSelector } from 'selectors/calendarSelectors';
import { formatReservationTime } from 'utils/timeUtils';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { getPartyTurnTimeMinutes } from 'utils/service/tableUtils';
import { isOrderVariety } from '@tock/shared/utils/offerings';
import { makeTableOccupancySelector } from 'components/service/party/TableOccupancySelector';
import { tableOccupancySelectorNoCache } from 'selectors/tableOccupancySelector';
import { toLocalDateTime } from '@tock/shared/utils/time';
import {
  trackAssignmentCancelled,
  trackAssignmentCompleted,
} from 'utils/service/tableAnalyticEvents';
import { useAppContext } from '@tock/coretypes';
import { useDrag } from 'react-dnd';
import { useEffect } from 'react';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useGATimer } from 'telemetry';
import { useIsInMobileMode } from 'hooks/useIsInMobileMode';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';

/**
 * These are the items from PartyDragData that come from
 * props on the draggable item.
 *
 * @todo - look in to where/how these configs are used
 */
type ServiceDragConfig = Pick<PartyDragData, 'fromTableId' | 'onSuccessfulDrop'>;

export type PartyDragItem = BookingDragItem | PartyDragData;

/**
 * Calculates a start date time string (in the business timezone) for a
 * party
 */
const getPartyStartTime = (party: Service.Party, timeZone: string) => {
  const { type, expectedReservationTime, seatedTime } = party;

  // @todo - we probably do this elsewhere, look for an existing util
  if (type === 'WalkIn' && seatedTime) {
    const seatedLocalDateTime = toLocalDateTime(Instant.ofEpochSecond(seatedTime), timeZone);
    const nearestStartTime = seatedLocalDateTime.minute() % 15;
    return formatReservationTime(seatedLocalDateTime.minusMinutes(nearestStartTime));
  }

  return expectedReservationTime;
};

/**
 * Hook to add draggable functionality to any UI item that
 * has access to a Party.
 *
 * @todo - Service and timeline drag actions have unique data requirements.
 *         Evaluate moving to a single item interface.
 */
const useDraggableParty = (
  party: Service.Party,
  dragSource: PartyDragSource,
  optionalServiceConfig?: ServiceDragConfig
) => {
  const gaTimer = useGATimer();
  const { url } = useRouteMatch<MatchParams>();
  const { context } = useAppContext();
  const { actions } = useActions();
  const hasUncachedCalendarSquares = useFeatureFlag('TIMELINE_UNCACHED_CALENDAR_SQUARES');

  const dateTimeString = getPartyStartTime(party, context.timeZone);

  const selectTableOccupancy = makeTableOccupancySelector();
  const tableOccupancyMap = useSelector((state: StoreType) =>
    hasUncachedCalendarSquares
      ? tableOccupancySelectorNoCache(state, dateTimeString, party.expectedTurnTimeMinutes ?? 15)
      : selectTableOccupancy(state, dateTimeString ?? '', party.expectedTurnTimeMinutes ?? 15)
  );

  const calendarState = useSelector(calendarStateSelector);
  const isInMobileMode = useIsInMobileMode();

  const [{ isDragging }, drag, preview] = useDrag<
    PartyDragItem,
    TableDropResult,
    { isDragging: boolean }
  >({
    type: DndItemTypes.PARTY,
    item: () => {
      const isInService = url.includes('service');

      if (isInService) {
        trackDragStart(dragSource, 'service');

        return {
          mode: 'SERVICE',
          id: party.id,
          name: party.name,
          party,
          dragSource,
          purchase: party.purchase,
          /**
           * @todo - Deprecate this config option when
           * TIMELINE_DRAG_TABLE_ASSIGNMENTS is launched
           * Now that the whole guest card is draggable
           * we don't use this offset
           */
          extraOffset: undefined,
          fromTableId: optionalServiceConfig?.fromTableId,
          onSuccessfulDrop: optionalServiceConfig?.onSuccessfulDrop,
        };
      }

      if (!dateTimeString) {
        return null;
      }

      actions.calendar.setFocusedTimeSpan(dateTimeString, party.expectedTurnTimeMinutes ?? 15);
      gaTimer.start(DRAG_ASSIGNMENT_TIMING.eventName);
      trackDragStart(dragSource, 'timeline');

      return {
        mode: 'TIMELINE',
        dateTimeString,
        name: party.name,
        partyId: party.id,
        turnTimeMinutes: getPartyTurnTimeMinutes(party) ?? 15,
        tableId: party.tableIds,
        purchaseId: party.purchase?.id,
        originalPurchaseId: party.purchase?.originalPurchaseId,
        tableOccupancyMap: tableOccupancyMap,
        isWalkin: party.type === 'WalkIn',
        purchase: party.purchase,
      };
    },
    end: (item, monitor) => {
      if (item.mode === 'TIMELINE') {
        actions.calendar.resetFocusedTimeSpan();

        const dropResult = monitor.getDropResult();
        if (dropResult) {
          gaTimer.finish(DRAG_ASSIGNMENT_TIMING.eventName, {
            category: DRAG_ASSIGNMENT_TIMING.category,
          });
          trackAssignmentCompleted('timeline', party.state, dropResult.status);
        } else {
          gaTimer.clear(DRAG_ASSIGNMENT_TIMING.eventName);
          trackAssignmentCancelled('timeline');
        }
      }
    },
    /**
     * Conditionally enable/disable drag for a party
     */
    canDrag: () => {
      const invalidDragStates: Service.PartyState[] = ['NO_SHOW', 'CANCELED', 'LEFT'];
      const isInTimeline = url.includes('calendar/day');
      const isSlot = !context.isFlexBusiness();
      const isDraggableParty = !isOrderVariety(party.purchase?.ticketType?.variety);

      if (
        (isSlot && isInTimeline) ||
        dragSource === 'PARTY_GROUP' ||
        calendarState === 'RESCHEDULE' ||
        isInMobileMode
      ) {
        return false;
      }

      return isDraggableParty && !invalidDragStates.includes(party.state);
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return { isDragging, drag };
};

export default useDraggableParty;
